import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFormik, FormikProvider} from 'formik';
import {shallowEqual, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {Line, OrderInvoiceFormValues} from '../../types';
import {InvoiceLine, RivhitCustomer, CurrentMonthInvoice} from '../../../../common/types';
import {RootState} from '../../../../store/configureStore';
import {isIsraeliIdValid} from "../../../../common/utils";
import {useUserSessionContext} from "../../../../common/components/core/UserSessionContext";
import {pathnames as P} from "../../../../common/enums";

export type OrderInvoiceFormContext = {
  initialValues: OrderInvoiceFormValues,
  defaultLine: InvoiceLine,
  searchCustomers: () => Promise<RivhitCustomer[]>,
  haveForcedCustomer: boolean,
  showSelectAction: boolean,
  handleSubmit: () => Promise<void>,
  invoices: CurrentMonthInvoice[],
  closePopupSelectAction: () => void,
  handleClick: () => Promise<void>,
  handleSubmitAdditional: () => Promise<void>,
  isSubmitting: boolean,
  showAllErrors: boolean
}

export interface OrderInvoiceFormContextProviderProps {
  children: React.ReactNode;
}

export const defaultLine: Line = {
  vatIncluded: false,
  amount: '',
  description: '',
};

export const defaultContext: OrderInvoiceFormContext = {
  initialValues: {
    customerName: '',
    idNumber: '',
    address: '',
    phoneFax: '',
    contact: '',
    emails: '',
    lines: [defaultLine]
  },
  defaultLine,
  searchCustomers: async () => ([]),
  haveForcedCustomer: false,
  showSelectAction: false,
  handleSubmit: async () => {},
  invoices: [],
  closePopupSelectAction: () => {},
  handleClick: async () => {},
  handleSubmitAdditional: async () => {},
  isSubmitting: false,
  showAllErrors: false
};

export const OrderInvoiceFormContext = createContext<OrderInvoiceFormContext>(defaultContext);
OrderInvoiceFormContext.displayName = 'OrderInvoiceFormContext';

export const useOrderInvoiceFormContext = () => useContext(OrderInvoiceFormContext);

export const OrderInvoiceFormContextProvider = ({children}: OrderInvoiceFormContextProviderProps) => {
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const user = useSelector(
    (state: RootState) => state.auth.user,
    shallowEqual
  );
  const userAgentID = user?.agentId ?? 0;
  const forcedCustomerID = user?.forcedCustomerID ?? 0;
  const userAgentFullName = [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ');

  const {invoiceOrderPageNotAllowed} = useUserSessionContext();
  const navigate = useNavigate();
  useEffect(() => {
    if (invoiceOrderPageNotAllowed) {
      navigate(P.HOME)
    }
  }, []);

  const formik = useFormik<OrderInvoiceFormValues>({
    initialValues: {...defaultContext.initialValues},
    validate: (values) => {
      let errors: Partial<OrderInvoiceFormValues> = {};
      if (!forcedCustomerID && !values.customerName) {
        errors = {...errors, customerName: M.requiredField};
      }
      if (!values.address) {
        errors = {...errors, address: M.requiredField};
      }
      if (!values.phoneFax) {
        errors = {...errors, phoneFax: M.requiredField};
      }
      if (!values.emails) {
        errors = {...errors, emails: M.requiredField};
      }
      const lineErrors = values.lines
        .map(line => {
          let lineErrors: Partial<InvoiceLine> = {};
          const amount = parseFloat(line.amount) || 0;
          if (amount < 0) {
            lineErrors = {...lineErrors, amount: M.requiredField};
          }
          if (!line.description) {
            lineErrors = {...lineErrors, description: M.requiredField};
          }
          if (!line.amount) {
            lineErrors = {...lineErrors, amount: M.requiredField};
          }

          return (Object.keys(lineErrors).length > 0) ? lineErrors : null;
        }).filter(Boolean);

      const totalAmount = values.lines
        .map(item => {
          if (item.amount) {
            return parseFloat(item.amount) || 0;
          } else {
            return 0;
          }
        })
        .reduce((a, b) => a + b, 0);

      if (totalAmount === 0) {
        errors = {...errors, totalAmount: M.totalAmountErrorMessage};
      }

      if (values.customerName?.trim() === userAgentFullName) {
        errors = {...errors, customerName: M.customerNameIsAgentNameError};
      }

      if (values.rivhitCustomerID?.trim() == userAgentID) {
        errors = {...errors, customerName: M.customerIdIsAgentIdError};
      }

      if (values.idNumber && !isIsraeliIdValid(values.idNumber) && values.idNumber !== '999999999') {
        errors = {...errors, idNumber: M.invalidIdNumberError};
      }

      if (totalAmount >= 5000 && values.idNumber !== '999999999') {
        if (!values.idNumber) {
          errors = {...errors, idNumber: M.requiredField};
        }
      }

      return (lineErrors.length > 0) ? {...errors, lines: lineErrors} : errors;
    },
    onSubmit: () => {},
    enableReinitialize: true,
  });
  const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<CurrentMonthInvoice[]>([]);
  const [showSelectAction, setShowSelectAction] = useState<boolean>(false);
  const showPopupSelectAction = () => setShowSelectAction(true);
  const closePopupSelectAction = () => setShowSelectAction(false);

  const handleSubmit = async () => {
    const {values, resetForm} = formik;
    await setIsSubmitting(true);
    try {
      const result = await apiService.orderInvoice({
        customerID: values.rivhitCustomerID,
        customerName: values.customerName,
        idNumber: values.idNumber,
        address: values.address,
        contact: values.contact,
        phone: values.phoneFax,
        emailRecipients: values.emails,
        lines: values.lines.map(item => ({...item, amount: parseFloat(item.amount)}))
      });
      if (result.errorCode === 0) {
        globalMessage.open(M.successCreatedInvoiceOrder);
        setShowAllErrors(false);
      }
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    } finally {
      resetForm();
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000)
    }
  };

  const searchCustomers = async () => {
    try {
      if (!userAgentID) {
        return [];
      }

      const result = await apiService.getCustomersByAgentID(parseInt(userAgentID, 10));
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      }

      return result.body || [];
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);

      return [];
    }
  };

  const getCurrentMonthInvoices = async () => {
    const idNumber = parseInt(formik.values.idNumber, 10);
    return idNumber
      ? await apiService.getCurrentMonthInvoices(idNumber)
      : {invoices: []};
  };

  const handleClick = async () => {
    if (formik.isValid) {
      if (formik.values.idNumber) {
        const {invoices} = await getCurrentMonthInvoices();
        setInvoices(invoices || []);
        if (invoices && invoices.length > 0) {
          showPopupSelectAction();
        } else {
          await handleSubmit();
        }
      } else {
        await handleSubmit();
      }
    } else {
      setShowAllErrors(true);
    }
  };

  const handleSubmitAdditional = async () => {
    try {
      await handleSubmit();
      closePopupSelectAction();
      globalMessage.open(M.createdAdditionalInvoice);
    } catch (e) {
      console.error(e)
      globalMessage.open(M.serverError);
    }
  };

  const context: OrderInvoiceFormContext = {
    ...defaultContext,
    searchCustomers,
    haveForcedCustomer: Boolean(forcedCustomerID),
    handleSubmit,
    invoices,
    showSelectAction,
    closePopupSelectAction,
    handleClick,
    handleSubmitAdditional,
    isSubmitting,
    showAllErrors
  };

  return (
    <OrderInvoiceFormContext.Provider value={context}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </OrderInvoiceFormContext.Provider>
  );
};
